import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar from '../components/SideBar';
import '../App.css';
import './artistPage.css';
import { editArtist, deleteArtist, uploadArtistProfilePicture } from './artistHelper.js';
import { getCategories, addCategory, getAllArt, delArt, addArt, editArt, updateArtStatus, updateApplicationStatus } from './artHelper.js';
import logoImage from '../images/Logo-Base-basic.png';
import { Table, Modal, Button } from 'react-bootstrap';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TextField from '@mui/material/TextField';
import { ENDPOINT_PATH } from '../config';
import { authFetch } from '../utils/authFetch';

const SUBMITTED_DATE_TZ = 'America/New_York';

function toDateFromFirestoreValue(value) {
  if (value == null) return null;
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const sec =
    typeof value._seconds === 'number'
      ? value._seconds
      : typeof value.seconds === 'number'
        ? value.seconds
        : null;
  if (sec != null) {
    const nano =
      typeof value._nanoseconds === 'number'
        ? value._nanoseconds
        : typeof value.nanoseconds === 'number'
          ? value.nanoseconds
          : 0;
    return new Date(sec * 1000 + Math.floor(nano / 1e6));
  }
  return null;
}

/** Display-only: maps Firestore/API timestamp shapes to calendar date in Eastern. */
function formatSubmittedDate(value) {
  const d = toDateFromFirestoreValue(value);
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: SUBMITTED_DATE_TZ,
    }).format(d);
  } catch {
    return '—';
  }
}

/** Milliseconds for sort: updatedAt, else createdAt (aligned with admin list ordering). */
function artworkTimeMs(artwork) {
  const u = toDateFromFirestoreValue(artwork.updatedAt);
  if (u) return u.getTime();
  const c = toDateFromFirestoreValue(artwork.createdAt);
  if (c) return c.getTime();
  return 0;
}

function artworkNormalizedString(artwork, fieldKey) {
  let v;
  if (fieldKey === 'artworkName') v = artwork.artworkName;
  else if (fieldKey === 'artistName') v = artwork.artistName;
  else v = artwork.category;
  return String(v ?? '').trim();
}

/** Empty strings sort last for both ascending and descending primary string sorts. */
function compareArtworkStringsForSort(a, b, fieldKey, ascending) {
  const sa = artworkNormalizedString(a, fieldKey);
  const sb = artworkNormalizedString(b, fieldKey);
  const aEmpty = sa === '';
  const bEmpty = sb === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base', numeric: true });
  return ascending ? cmp : -cmp;
}

function compareArtworksForAdminTable(a, b, primaryKey, primaryDir) {
  const asc = primaryDir === 'asc';
  if (primaryKey === 'submittedDate') {
    const diff = artworkTimeMs(b) - artworkTimeMs(a);
    if (!asc) {
      if (diff !== 0) return diff;
    } else if (diff !== 0) {
      return -diff;
    }
  } else {
    const strCmp = compareArtworkStringsForSort(a, b, primaryKey, asc);
    if (strCmp !== 0) return strCmp;
    const timeDiff = artworkTimeMs(b) - artworkTimeMs(a);
    if (timeDiff !== 0) return timeDiff;
  }
  return String(b.artDocID || '').localeCompare(String(a.artDocID || ''));
}

function sortArtworkBucket(list, primaryKey, primaryDir) {
  return [...list].sort((a, b) => compareArtworksForAdminTable(a, b, primaryKey, primaryDir));
}

function tableColumnAriaSort(activeKey, activeDir, columnKey) {
  if (activeKey !== columnKey) return 'none';
  return activeDir === 'asc' ? 'ascending' : 'descending';
}

function artistDisplayName(artist) {
  return `${artist.firstName ?? ''} ${artist.lastName ?? ''}`.trim();
}

function compareArtistStringsForSort(a, b, fieldKey, ascending) {
  const sa = fieldKey === 'name' ? artistDisplayName(a) : String(a.email ?? '').trim();
  const sb = fieldKey === 'name' ? artistDisplayName(b) : String(b.email ?? '').trim();
  const aEmpty = sa === '';
  const bEmpty = sb === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base', numeric: true });
  return ascending ? cmp : -cmp;
}

function compareArtistsForAdminTable(a, b, primaryKey, primaryDir) {
  const asc = primaryDir === 'asc';
  const strCmp = compareArtistStringsForSort(a, b, primaryKey, asc);
  if (strCmp !== 0) return strCmp;
  return String(b.id || '').localeCompare(String(a.id || ''));
}

function sortArtistBucket(list, primaryKey, primaryDir) {
  return [...list].sort((a, b) => compareArtistsForAdminTable(a, b, primaryKey, primaryDir));
}

function applicationTimeMs(app) {
  const u = toDateFromFirestoreValue(app.updatedAt);
  if (u) return u.getTime();
  const c = toDateFromFirestoreValue(app.createdAt);
  if (c) return c.getTime();
  return 0;
}

function compareApplicationStringsForSort(a, b, fieldKey, ascending) {
  const sa = fieldKey === 'name' ? String(a.name ?? '').trim() : String(a.email ?? '').trim();
  const sb = fieldKey === 'name' ? String(b.name ?? '').trim() : String(b.email ?? '').trim();
  const aEmpty = sa === '';
  const bEmpty = sb === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base', numeric: true });
  return ascending ? cmp : -cmp;
}

function compareApplicationsForAdminTable(a, b, primaryKey, primaryDir) {
  const asc = primaryDir === 'asc';
  if (primaryKey === 'submittedDate') {
    const diff = applicationTimeMs(b) - applicationTimeMs(a);
    if (!asc) {
      if (diff !== 0) return diff;
    } else if (diff !== 0) {
      return -diff;
    }
  } else {
    const strCmp = compareApplicationStringsForSort(a, b, primaryKey, asc);
    if (strCmp !== 0) return strCmp;
    const timeDiff = applicationTimeMs(b) - applicationTimeMs(a);
    if (timeDiff !== 0) return timeDiff;
  }
  return String(b.id || '').localeCompare(String(a.id || ''));
}

function sortApplicationBucket(list, primaryKey, primaryDir) {
  return [...list].sort((a, b) => compareApplicationsForAdminTable(a, b, primaryKey, primaryDir));
}

function ArtworkSortHeaderButton({ label, sortKey, activeKey, activeDir, onSort }) {
  const isPrimary = activeKey === sortKey;
  const showNeutral = !isPrimary;

  let arrows;
  if (showNeutral) {
    arrows = (
      <span className="artwork-sort-arrows artwork-sort-arrows--neutral" aria-hidden>
        <span className="artwork-sort-arrow-up">▲</span>
        <span className="artwork-sort-arrow-down">▼</span>
      </span>
    );
  } else if (activeDir === 'asc') {
    arrows = (
      <span className="artwork-sort-arrows" aria-hidden>
        <span className="artwork-sort-arrow-up artwork-sort-arrow-up--solo">▲</span>
      </span>
    );
  } else {
    arrows = (
      <span className="artwork-sort-arrows" aria-hidden>
        <span className="artwork-sort-arrow-down artwork-sort-arrow-down--solo">▼</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      className="artwork-sort-btn"
      onClick={() => onSort(sortKey)}
    >
      <span>{label}</span>
      {arrows}
    </button>
  );
}

const AdminNavbar = () => {
  return (
    <div className="navWrapper">
      <div className="AdminNav">
        <div>
          <a href="/" className="NavLink">
            <div className="AdminLogo d-flex justify-content-end">
              <img src={logoImage} alt='logo' className="NavLogo" />
            </div>
            <div className="AdminLogoText font-2 ps-2 fontLogo">mycustomframe</div>
          </a>
        </div>
      </div>
    </div>
  );
};


const ArtistPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // 'artists' | 'artwork' | 'applications'
  const [activeTab, setActiveTab] = useState('artists');

  // artist applications
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationsError, setApplicationsError] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  //-------------------

  // State for artists, artworks, and categories
  const [artists, setArtists] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [itemsPerPage] = useState(7);
  /** Sub-tabs within Artwork Profile / Applications (one table visible at a time — avoids layout jump from stacked sections) */
  const [artworkStatusTab, setArtworkStatusTab] = useState('pending');
  const [applicationStatusTab, setApplicationStatusTab] = useState('pending');
  const [artworkListPage, setArtworkListPage] = useState(1);
  const [applicationListPage, setApplicationListPage] = useState(1);
  /** Artwork Profile table: primary sort key + direction (default: submitted date, newest first). */
  const [artworkSort, setArtworkSort] = useState({ key: 'submittedDate', dir: 'desc' });
  /** Artist Profile table: sort by name or email. */
  const [artistSort, setArtistSort] = useState({ key: 'name', dir: 'asc' });
  /** Artist Applications table: name, email, or submitted date. */
  const [applicationSort, setApplicationSort] = useState({ key: 'submittedDate', dir: 'desc' });

  const [userType, setUserType] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const isEmployee = userType === 'employee';

  useEffect(() => {
    const fetchUserType = async () => {
      try {
        if (user && user.uid) {
          const response = await fetch(
            `${ENDPOINT_PATH}/api/get-account-type?user=${encodeURIComponent(user.uid)}`
          );
          const accountType = await response.json();
          setUserType(String(accountType).toLowerCase());
        }
      } catch (error) {
        console.error('Error fetching user type:', error);
      }
    };
    fetchUserType();
  }, [user]);

  useEffect(() => {
    // Fetch artists data
    const fetchArtists = async () => {
      try {
        const response = await fetch(`${ENDPOINT_PATH}/api/get-artists`);
        const data = await response.json();
        setArtists(data.data);
      } catch (error) {
        console.error("Error fetching artists:", error);
      }
    };

    // Fetch artwork data - use adminView=true to see all artwork (pending/approved/rejected)
    const fetchArtworks = async () => {
      const data = await getAllArt(true); // true = admin view (see all artwork)
      if (data && data.data) {
        setArtworks(data.data);
      }
    };

    // Fetch categories 
    const fetchCategories = async () => {
      const categories = await getCategories();
      if (categories && categories.data) {
        setCategories(categories.data);
        console.log("categories", categories.data);
      }
    };

    fetchArtists();
    fetchArtworks();
    fetchCategories();

  }, []); // Empty dependency array ensures this runs only once when the component is mounted

  useEffect(() => {
    if (activeTab !== 'applications') return;

    // Fetch applications from the database when the applications tab is activated
    const fetchApplications = async () => {
      try {
        setApplicationsLoading(true);
        setApplicationsError('');
        const url = `${ENDPOINT_PATH}/api/artist-applications`;
        console.log('Fetching artist applications from:', url);

        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) {
          let errorMessage = `Server error: ${res.status}`;
          try {
            const errorData = await res.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch (e) {
            // If response is not JSON, use status text
            errorMessage = res.statusText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const data = await res.json();
        console.log('Artist applications response:', data);
        console.log('Applications data:', data.data);

        // Handle different response formats
        if (data && Array.isArray(data)) {
          // If data is directly an array
          setApplications(data);
        } else if (data.success && Array.isArray(data.data)) {
          setApplications(data.data);
        } else if (Array.isArray(data.data)) {
          setApplications(data.data);
        } else {
          console.warn('Unexpected data format:', data);
          setApplications([]);
        }
      } catch (err) {
        console.error('Error fetching artist applications:', err);
        let errorMessage = 'Error loading artist applications. ';
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          errorMessage += 'Please check if the backend server is running.';
        } else {
          errorMessage += err.message;
        }
        setApplicationsError(errorMessage);
      } finally {
        setApplicationsLoading(false);
      }
    };

    fetchApplications();
  }, [activeTab]);

  const [editingArtist, setEditingArtist] = useState(null); // Track artist being edited
  const [editingArtwork, setEditingArtwork] = useState(null); // Track artwork being edited

  const refreshArtworks = useCallback(async () => {
    try {
      const data = await getAllArt(true);
      if (data && data.data) {
        setArtworks(data.data);
      }
    } catch (e) {
      console.error('Error refreshing artworks:', e);
    }
  }, []);

  const normalizeArtworkStatus = (status) => {
    const s = (status || 'pending').toLowerCase();
    if (s === 'approved') return 'approved';
    if (s === 'rejected') return 'rejected';
    return 'pending';
  };

  const artworkMatchesSearch = useCallback((artwork) => {
    const q = searchQuery.toLowerCase();
    return (
      (artwork.artworkName || '').toLowerCase().includes(q) ||
      (artwork.artistName || '').toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const applicationMatchesSearch = useCallback((app) => {
    const q = searchQuery.toLowerCase();
    return (
      (app.name || '').toLowerCase().includes(q) ||
      (app.email || '').toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const handleArtworkSortClick = useCallback((clickedKey) => {
    setArtworkListPage(1);
    setArtworkSort((prev) => {
      if (clickedKey === 'submittedDate') {
        if (prev.key === 'submittedDate') {
          return { key: 'submittedDate', dir: prev.dir === 'asc' ? 'desc' : 'asc' };
        }
        return { key: 'submittedDate', dir: 'desc' };
      }
      if (prev.key === clickedKey) {
        return { key: clickedKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: clickedKey, dir: 'asc' };
    });
  }, []);

  const handleArtistSortClick = useCallback((clickedKey) => {
    setCurrentPage(1);
    setArtistSort((prev) => {
      if (prev.key === clickedKey) {
        return { key: clickedKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: clickedKey, dir: 'asc' };
    });
  }, []);

  const handleApplicationSortClick = useCallback((clickedKey) => {
    setApplicationListPage(1);
    setApplicationSort((prev) => {
      if (clickedKey === 'submittedDate') {
        if (prev.key === 'submittedDate') {
          return { key: 'submittedDate', dir: prev.dir === 'asc' ? 'desc' : 'asc' };
        }
        return { key: 'submittedDate', dir: 'desc' };
      }
      if (prev.key === clickedKey) {
        return { key: clickedKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: clickedKey, dir: 'asc' };
    });
  }, []);

  const artworkSections = useMemo(() => {
    const pending = [];
    const approved = [];
    const rejected = [];
    for (const artwork of artworks) {
      if (!artworkMatchesSearch(artwork)) continue;
      const bucket = normalizeArtworkStatus(artwork.status);
      if (bucket === 'approved') approved.push(artwork);
      else if (bucket === 'rejected') rejected.push(artwork);
      else pending.push(artwork);
    }
    const { key, dir } = artworkSort;
    return {
      pending: sortArtworkBucket(pending, key, dir),
      approved: sortArtworkBucket(approved, key, dir),
      rejected: sortArtworkBucket(rejected, key, dir),
    };
  }, [artworks, artworkMatchesSearch, artworkSort]);

  const artistMatchesSearch = useCallback((artist) => {
    const q = searchQuery.toLowerCase();
    return `${artist.firstName} ${artist.lastName}`.toLowerCase().includes(q);
  }, [searchQuery]);

  const filteredArtists = useMemo(() => {
    const list = artists.filter(artistMatchesSearch);
    return sortArtistBucket(list, artistSort.key, artistSort.dir);
  }, [artists, artistMatchesSearch, artistSort]);

  const applicationSections = useMemo(() => {
    const pending = [];
    const approved = [];
    const rejected = [];
    for (const app of applications) {
      if (!applicationMatchesSearch(app)) continue;
      const bucket = (app.status || 'pending').toLowerCase();
      if (bucket === 'approved') approved.push(app);
      else if (bucket === 'rejected') rejected.push(app);
      else pending.push(app);
    }
    const { key, dir } = applicationSort;
    return {
      pending: sortApplicationBucket(pending, key, dir),
      approved: sortApplicationBucket(approved, key, dir),
      rejected: sortApplicationBucket(rejected, key, dir),
    };
  }, [applications, applicationMatchesSearch, applicationSort]);

  useEffect(() => {
    setArtworkListPage(1);
    setApplicationListPage(1);
  }, [searchQuery]);

  // Calculate total pages for Artist Profile tab only
  const getCurrentDataLength = () => filteredArtists.length;

  const totalPages = Math.max(1, Math.ceil(getCurrentDataLength() / itemsPerPage));

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const STATUS_SUBTABS = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' }
  ];

  const handleEditArtist = (artist) => {
    if (isEmployee) return;
    setEditingArtist(artist); // Set the artist being edited
    setShowArtistModal(true); // Open the modal
  };

  const handleDeleteArtist = (artist) => {
    if (isEmployee) return;
    if (window.confirm(`Are you sure you want to delete ${artist.firstName} ${artist.lastName}?`)) {
      deleteArtist(artist.firstName, artist.lastName);
      setArtists((prevArtists) => prevArtists.filter((a) => a.id !== artist.id));
    }
  }

  const handleDeleteArtwork = (artwork) => {
    if (isEmployee) return;
    // Open reason modal instead of window.confirm
    setPendingAction({
      type: 'delete',
      artwork: artwork
    });
    setShowReasonModal(true);
  };

  const handleUpdateArtStatus = async (artwork, newStatus) => {
    if (isEmployee) return;
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || !user.uid) {
      alert('Admin authentication required. Please log in again.');
      return;
    }

    const currentStatus = (artwork.status || 'pending').toLowerCase();

    // If rejecting, open reason modal instead of window.confirm
    if (newStatus === 'rejected') {
      setPendingAction({
        type: 'reject',
        artwork: artwork,
        newStatus: newStatus
      });
      setShowReasonModal(true);
      return; // Exit early - modal will handle the rest
    }

    // For approve actions, use existing window.confirm (no reason needed)

    // Confirmation messages based on current status and new status
    let confirmMessage = '';
    if (currentStatus === 'pending' && newStatus === 'approved') {
      confirmMessage = `Are you sure you want to approve "${artwork.artworkName}"?`;
    } else if (currentStatus === 'rejected' && newStatus === 'approved') {
      confirmMessage = `Are you sure you want to change "${artwork.artworkName}" from Rejected to Approved?`;
    } else if (currentStatus === 'approved' && newStatus === 'approved') {
      alert('Artwork is already approved.');
      return;
    } else if (currentStatus === 'rejected' && newStatus === 'rejected') {
      alert('Artwork is already rejected.');
      return;
    }

    // Show confirmation dialog for approve actions
    if (confirmMessage) {
      const confirmed = window.confirm(confirmMessage);
      if (!confirmed) {
        return;
      }
    }

    try {
      await updateArtStatus(
        user.uid,
        artwork.artistDocID,
        artwork.artDocID,
        newStatus
      );

      // Send decision email to artist
      try {
        await fetch(`${ENDPOINT_PATH}/api/artwork-decision-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            artistDocId: artwork.artistDocID,
            artDocId: artwork.artDocID,
            newStatus: newStatus
          })
        });
      } catch (emailError) {
        console.error('Error sending artwork decision email:', emailError);
        // Do not block the status update if email fails
      }

      await refreshArtworks();

      // Show success message
      let statusMessage = '';
      if (currentStatus === 'pending') {
        statusMessage = 'approved';
      } else {
        statusMessage = `status changed from ${currentStatus} to ${newStatus}`;
      }
      alert(`Artwork "${artwork.artworkName}" has been ${statusMessage}!`);
    } catch (error) {
      console.error('Error updating artwork status:', error);
      alert(`Error updating artwork status: ${error.message || 'Please try again.'}`);
    }
  };

  const handleUpdateApplicationStatus = async (application, newStatus, reason = '') => {
    if (isEmployee) return;
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || !user.uid) {
      alert('Admin authentication required. Please log in again.');
      return;
    }

    const currentStatus = (application.status || 'pending').toLowerCase();

    // If rejecting, open reason modal instead of window.confirm
    if (newStatus === 'rejected') {
      setPendingAction({
        type: 'rejectApplication',
        application: application,
        newStatus: newStatus
      });
      setShowReasonModal(true);
      return; // Exit early - modal will handle the rest
    }

    // Confirmation messages based on current status and new status
    let confirmMessage = '';
    if (currentStatus === 'pending' && newStatus === 'approved') {
      confirmMessage = `Are you sure you want to approve "${application.name}"'s application?`;
    } else if (currentStatus === 'approved' && newStatus === 'rejected') {
      confirmMessage = `Are you sure you want to change "${application.name}"'s application from Approved to Rejected?`;
    } else if (currentStatus === 'rejected' && newStatus === 'approved') {
      confirmMessage = `Are you sure you want to change "${application.name}"'s application from Rejected to Approved?`;
    } else if (currentStatus === 'approved' && newStatus === 'approved') {
      alert('Application is already approved.');
      return;
    } else if (currentStatus === 'rejected' && newStatus === 'rejected') {
      alert('Application is already rejected.');
      return;
    }

    // Show confirmation dialog for all non-rejection status changes
    if (confirmMessage) {
      const confirmed = window.confirm(confirmMessage);
      if (!confirmed) {
        return;
      }
    }

    try {
      await updateApplicationStatus(
        user.uid,
        application.id,
        newStatus
      );

      // Send decision email to applicant when approved or rejected
      if (newStatus === 'approved' || newStatus === 'rejected') {
        try {
          await fetch(`${ENDPOINT_PATH}/api/artist-application-decision`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              applicationId: application.id,
              newStatus: newStatus,
              reason: reason
            })
          });
        } catch (emailError) {
          console.error('Error sending decision email:', emailError);
          // Do not block the status update if email fails
        }
      }

      // Update local state
      setApplications((prevApplications) =>
        prevApplications.map((a) =>
          a.id === application.id
            ? { ...a, status: newStatus }
            : a
        )
      );

      // Refresh applications list to get updated data
      try {
        const res = await fetch(`${ENDPOINT_PATH}/api/artist-applications`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && Array.isArray(data.data)) {
            setApplications(data.data);
          } else if (Array.isArray(data.data)) {
            setApplications(data.data);
          }
        }
      } catch (refreshError) {
        console.error('Error refreshing applications:', refreshError);
      }

      // Show success message
      let statusMessage = '';
      if (currentStatus === 'pending') {
        statusMessage = newStatus === 'approved' ? 'approved' : 'rejected';
      } else {
        statusMessage = `status changed from ${currentStatus} to ${newStatus}`;
      }
      alert(`Application from "${application.name}" has been ${statusMessage}!`);
    } catch (error) {
      console.error('Error updating application status:', error);
      alert(`Error updating application status: ${error.message || 'Please try again.'}`);
    }
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    const statusLower = status?.toLowerCase() || 'pending';
    switch (statusLower) {
      case 'approved':
        return 'badge bg-success';
      case 'pending':
        return 'badge bg-warning text-dark';
      case 'rejected':
        return 'badge bg-danger';
      default:
        return 'badge bg-secondary';
    }
  };

  // Helper function to get status text
  const getStatusText = (status) => {
    const statusLower = status?.toLowerCase() || 'pending';
    switch (statusLower) {
      case 'approved':
        return 'Approved';
      case 'pending':
        return 'Pending';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Pending';
    }
  };

  const handleEditArtwork = (artwork) => {
    if (isEmployee) return;
    setEditingArtwork(artwork); // Set the artwork being edited
    setShowArtworkModal(true); // Open the modal
  };

  const handleImageUpload = (e, setImageState, setFileState) => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageState(reader.result);
      };
      reader.readAsDataURL(file);
      setFileState(file);
    }
  };

  const [showArtistModal, setShowArtistModal] = useState(false);

  // Reason modal state
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reasonText, setReasonText] = useState('');
  const [pendingAction, setPendingAction] = useState(null); // { type: 'reject' | 'delete' | 'rejectApplication', artwork/application: {...}, newStatus?: string }

  const ArtistProfileModal = ({ handleImageUpload }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [bio, setBio] = useState('');
    const [password, setPassword] = useState('');
    // image url
    const [uploadedImage, setUploadedImage] = useState(null);
    // file
    const [uploadedFile, setUploadedFile] = useState(null);
    const [initalArtist, setInitalArtist] = useState({});
    const [createArtistLoading, setCreateArtistLoading] = useState(false);
    const [createArtistError, setCreateArtistError] = useState('');

    const user = JSON.parse(localStorage.getItem('user'));

    const handlePhoneChange = (e) => {
      let rawValue = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
      if (rawValue.length > 10) rawValue = rawValue.slice(0, 10); // Limit to 10 digits

      // Format to (XXX)XXX-XXXX
      const formattedValue = rawValue.replace(
        /^(\d{3})(\d{0,3})(\d{0,4})$/,
        (_, areaCode, prefix, lineNumber) =>
          `(${areaCode}) ${prefix}${lineNumber ? `-${lineNumber}` : ''}`
      );

      setPhoneNumber(formattedValue);
    };

    useEffect(() => {
      // Reset form when editingArtist changes or modal opens
      if (editingArtist) {
        setFirstName(editingArtist.firstName || '');
        setLastName(editingArtist.lastName || '');
        setEmail(editingArtist.email || '');
        setPhoneNumber(editingArtist.phoneNumber || '');
        setBio(editingArtist.artistBio || '');
        setUploadedImage(editingArtist.artistPictureURL || null);
        setInitalArtist(editingArtist);
        setPassword(''); // Clear password when editing
      } else {
        // Clear all fields when creating new artist
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhoneNumber('');
        setBio('');
        setPassword('');
        setUploadedImage(null);
        setUploadedFile(null);
        setCreateArtistError('');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingArtist]);

    const detectChanges = (inital, updated) => {
      let changes = {};
      let profilePictureFile = null;

      if (inital.artistBio !== updated.artistBio) changes.artistBio = updated.artistBio;
      if (inital.email !== updated.email) changes.email = updated.email;
      if (inital.firstName !== updated.firstName) changes.firstName = updated.firstName;
      if (inital.lastName !== updated.lastName) changes.lastName = updated.lastName;
      if (inital.phoneNumber !== updated.phoneNumber) changes.phoneNumber = updated.phoneNumber;
      if (inital.artistPictureURL !== updated.artistPictureURL) {
        profilePictureFile = uploadedFile;
      }
      return ({ changes, profilePictureFile });
    };

    const handleClose = () => {
      setShowArtistModal(false);
      setUploadedImage(null); // Reset uploaded image
      setUploadedFile(null);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhoneNumber('');
      setBio('');
      setPassword('');
      setCreateArtistError('');
      setCreateArtistLoading(false);
    };

    const handleSave = async (e) => {
      e.preventDefault();
      if (isEmployee) return;

      if (!firstName || !lastName || !email || !phoneNumber) {
        alert('Please fill out all required fields.');
        return;
      }

      const form = document.getElementById('artistForm');
      // Check form validity
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }


      if (editingArtist) {
        // Editing existing artist - use existing flow
        const updatedArtist = {
          id: editingArtist.id,
          firstName: firstName,
          lastName: lastName,
          email: email,
          phoneNumber: phoneNumber,
          artistBio: bio,
          artistPictureURL: uploadedImage
        };

        const { changes, profilePictureFile } = detectChanges(initalArtist, updatedArtist);

        //API CALL
        editArtist(updatedArtist.id, changes, profilePictureFile);

        // update ui
        setArtists((prevArtists) =>
          prevArtists.map((artist) =>
            artist.id === updatedArtist.id ? updatedArtist : artist
          )
        );
        setEditingArtist(null); // Clear editing state
        setShowArtistModal(false);
      } else {
        // Creating new artist
        if (!password) {
          alert('Password is required to create an artist account.');
          return;
        }

        if (password.length < 6) {
          alert('Password must be at least 6 characters.');
          return;
        }

        setCreateArtistLoading(true);
        setCreateArtistError('');

        try {
          // Step 1: Upload profile picture if provided
          let artistPictureURL = '';
          if (uploadedFile) {
            try {
              const imageUrl = await uploadArtistProfilePicture(uploadedFile);
              // Parse JSON string to get the actual URL (API returns JSON string like "https://...")
              if (imageUrl) {
                try {
                  artistPictureURL = JSON.parse(imageUrl);
                } catch (parseError) {
                  // If JSON.parse fails, assume imageUrl is already the URL string
                  artistPictureURL = imageUrl;
                }
              }
            } catch (error) {
              console.error('Error uploading profile picture:', error);
              artistPictureURL = '';
            }
          }

          // Step 2: Create artist account via new endpoint
          const response = await authFetch(ENDPOINT_PATH + '/api/admin/create-artist', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: email,
              password: password,
              firstName: firstName,
              lastName: lastName,
              phoneNumber: phoneNumber,
              artistBio: bio || '',
              artistPictureURL: artistPictureURL
            }),
          });

          const data = await response.json();

          if (response.ok) {
            // Show success message with credentials
            alert(`Artist account created successfully!\n\nEmail: ${data.email}\nPassword: ${password}\n\nPlease save these credentials and share with the artist.`);

            // Reset form
            setFirstName('');
            setLastName('');
            setEmail('');
            setPhoneNumber('');
            setBio('');
            setPassword('');
            setUploadedImage(null);
            setUploadedFile(null);
            setShowArtistModal(false);

            // Refresh artist list
            try {
              const fetchResponse = await fetch(`${ENDPOINT_PATH}/api/get-artists`);
              const fetchData = await fetchResponse.json();
              if (fetchData && fetchData.data) {
                setArtists(fetchData.data);
              }
            } catch (error) {
              console.error('Error refreshing artist list:', error);
            }
          } else {
            setCreateArtistError(data.message || 'Failed to create artist account');
          }
        } catch (error) {
          console.error('Error creating artist account:', error);
          setCreateArtistError('Error creating artist account. Please try again.');
        } finally {
          setCreateArtistLoading(false);
        }
      }
    };

    return (
      <Modal show={showArtistModal} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingArtist ? 'Artist Profile' : 'New Artist Profile'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {/* Left Side: Form */}
            <div className="col-md-6">
              <form id="artistForm">
                <div className="form-row">
                  {/*First Name */}
                  <div className="form-group col-md-6">
                    <label>First Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      disabled={isEmployee}
                    />
                  </div>

                  {/*Last Name */}
                  <div className="form-group col-md-6">
                    <label>Last Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      disabled={isEmployee}
                    />
                  </div>

                  {/*Email*/}
                </div>
                <div className="form-group">
                  <label>Email <span className="text-danger">*</span></label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isEmployee}
                  />
                </div>

                {/*Phone Number*/}
                <div className="form-group">
                  <label>Phone Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="(123)123-1234"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    pattern="^\(\d{3}\) \d{3}-\d{4}$"
                    required
                    disabled={isEmployee}
                  />
                </div>

                {/*Password - Only show when creating new artist*/}
                {!editingArtist && (
                  <div className="form-group">
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      variant="outlined"
                      required
                      helperText="Minimum 6 characters"
                      className="mb-3"
                      disabled={isEmployee}
                    />
                  </div>
                )}

                {/*Bio*/}
                <div className="form-group">
                  <label>Artist Bio</label>
                  <textarea
                    className="form-control"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    disabled={isEmployee}
                  />
                </div>

                {/*Error message*/}
                {createArtistError && (
                  <div className="alert alert-danger mt-3">{createArtistError}</div>
                )}

                {/*Info message for new artist creation*/}
                {!editingArtist && (
                  <div className="alert alert-info mt-3">
                    <small>
                      <strong>ℹ️ Note:</strong> The artist will be able to login immediately with these credentials.
                      Make sure to save and share the password securely.
                    </small>
                  </div>
                )}
              </form>
            </div>

            {/* Right Side: Upload Profile Picture */}
            <div className="col-md-6 d-flex flex-column align-items-center">
              <label>Artist Profile Picture</label>
              <div className="placeholder-pfp">
                {uploadedImage ? (
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (<span ></span>)}
              </div>
              {!isEmployee && (
                <label className="btn btn-primary mb-2" style={{ cursor: 'pointer' }}>
                  Upload Picture
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, setUploadedImage, setUploadedFile)}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          {!isEmployee && (
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={createArtistLoading || (!editingArtist && (!password || password.length < 6))}
            >
              {createArtistLoading ? 'Creating...' : (editingArtist ? 'Save' : 'Create Artist')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );

  };

  const [showArtworkModal, setShowArtworkModal] = useState(false);

  const ArtworkModal = ({ handleImageUpload, onArtworksMutated }) => {
    const [artworkName, setArtworkName] = useState('');
    const [selectedArtist, setSelectedArtist] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');

    const [description, setDescription] = useState('');
    // image url
    const [uploadedArtwork, setUploadedArtwork] = useState(null);
    // file
    const [uploadedFile, setUploadedFile] = useState(null);

    const [initialArtwork, setInitalArtwork] = useState({});

    const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
    const [newCategory, setNewCategory] = useState("");

    const handleCategoryChange = (e) => {
      const value = e.target.value;
      if (value === "New Category") {
        setSelectedCategory(value);
        setShowNewCategoryInput(true);
      } else {
        setShowNewCategoryInput(false);
        setSelectedCategory(value);
      }
    };

    useEffect(() => {
      if (editingArtwork) {
        setArtworkName(editingArtwork.artworkName || '');
        setSelectedArtist(editingArtwork.artistName || '');
        setSelectedCategory(editingArtwork.category || '');

        setDescription(editingArtwork.artDescription || '');
        setUploadedArtwork(editingArtwork.artImageUrl || null);

        setInitalArtwork(editingArtwork);

      }
    }, []);

    const detectChanges = (initial, updated) => {
      let changes = {};
      let imageFile = null;
      let selectedArtist = null;

      if (initial.artworkName !== updated.artworkName) changes.artworkName = updated.artworkName;

      if (initial.category !== updated.category) changes.category = updated.category;
      if (initial.artDescription !== updated.artDescription) changes.artDescription = updated.artDescription;

      // check if artist changed
      if (initial.artistName !== updated.artistName) {
        selectedArtist = updated.artistName
      }

      // Check if the image has changed
      if (initial.artImageUrl !== updated.artImageUrl) {
        imageFile = uploadedFile; // Assuming uploadedArtwork contains the new image file or URL
      }

      return { changes, selectedArtist, imageFile };
    };


    const handleSaveArtwork = async (e) => {
      e.preventDefault();
      if (isEmployee) return;

      if (!artworkName || !selectedArtist || !uploadedArtwork) {
        alert('Please fill out all fields.');
        return;
      }

      const form = document.getElementById('artworkForm');
      // Check form validity
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      if (newCategory && showNewCategoryInput) {
        // API Call 
        addCategory(newCategory);
        setSelectedCategory(newCategory);
        setCategories((prevCategories) => [...prevCategories, { category: newCategory }]);
      }

      // Create updated artwork object
      const updatedArtwork = {
        artDocID: editingArtwork ? editingArtwork.artDocID : Date.now(),
        artistDocID: editingArtwork ? editingArtwork.artistDocID : Date.now(),
        artworkName: artworkName,
        artistName: selectedArtist,

        category: selectedCategory,
        artDescription: description,
        artImageUrl: uploadedArtwork, // Assuming `uploadedArtwork` contains image URL or file path
      };

      try {
        if (editingArtwork) {
          const { changes, selectedArtist: selArtist, imageFile } = detectChanges(initialArtwork, updatedArtwork);
          console.log(updatedArtwork);
          await editArt(updatedArtwork.artDocID, updatedArtwork.artistDocID, selArtist, imageFile, changes);
          setEditingArtwork(null);
        } else {
          await addArt(artworkName, selectedArtist, selectedCategory, description, uploadedFile);
          console.log(updatedArtwork);
        }
        await onArtworksMutated?.();
        setShowArtworkModal(false);
      } catch (err) {
        console.error(err);
        alert(err.message || 'Could not save artwork. Please try again.');
      }
    };


    const handleCloseArtworkModal = () => {
      setShowArtworkModal(false);
      setUploadedArtwork(null);
      setUploadedFile(null);
      setArtworkName('');
      setSelectedArtist('');
      setSelectedCategory('');

      setDescription('');
      setShowNewCategoryInput(false);
      setNewCategory("");
    };



    return (
      <Modal show={showArtworkModal} onHide={handleCloseArtworkModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingArtwork ? 'Artwork' : 'New Artwork'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {/* Left Side */}
            <div className="col-md-6">
              <form id="artworkForm">
                {/*Artwork Name*/}
                <div className="form-group">
                  <label>Artwork Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={artworkName}
                    onChange={(e) => setArtworkName(e.target.value)}
                    required
                    disabled={isEmployee}
                  />
                </div>

                {/*Select Artist*/}
                <div className="form-group">
                  <label>Select Artist <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={selectedArtist}
                    onChange={(e) => setSelectedArtist(e.target.value)}
                    required
                    disabled={isEmployee}
                  >
                    <option></option>
                    {artists.map((artist) => (
                      <option key={artist.id} value={`${artist.firstName} ${artist.lastName}`}>
                        {`${artist.firstName} ${artist.lastName}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/*Select Category*/}
                <div className="form-group">
                  <label>Select Category</label>
                  <select
                    className="form-select"
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    disabled={isEmployee}
                  >
                    <option value="" disabled>Select a category</option>
                    <option value="New Category">New Category</option>
                    {categories.map((item, index) => (
                      <option key={index} value={item.category}>
                        {item.category}
                      </option>
                    ))}
                  </select>
                </div>

                {/*If New Category was selected...*/}
                {showNewCategoryInput && (
                  <div className="form-group">
                    <label>New Category <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={newCategory}
                      onChange={(e) => { setNewCategory(e.target.value); setSelectedCategory(e.target.value) }}
                      required
                      disabled={isEmployee}
                    />
                  </div>
                )}



                {/*Description*/}
                <div className="form-group">
                  <label>Artwork Description</label>
                  <textarea
                    className="form-control"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isEmployee}
                  />
                </div>
              </form>
            </div>

            {/* Right Side: Upload Artwork */}
            <div className="col-md-6 d-flex flex-column align-items-center">
              <label> Artwork <span className="text-danger">*</span></label>
              <div className="placeholder-artwork">
                {uploadedArtwork ? (
                  <img
                    src={uploadedArtwork}
                    alt="Uploaded"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (<span ></span>)}
              </div>
              {!isEmployee && (
                <label className="btn btn-primary mb-2" style={{ cursor: 'pointer' }}>
                  Upload Artwork
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, setUploadedArtwork, setUploadedFile)}
                    style={{ display: 'none' }}
                    required
                  />
                </label>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseArtworkModal}>
            Cancel
          </Button>
          {!isEmployee && (
            <Button variant="primary" onClick={handleSaveArtwork}>
              Save Changes
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }

  // Reason Modal Handlers
  const handleCloseReasonModal = () => {
    setShowReasonModal(false);
    setReasonText('');
    setPendingAction(null);
  };

  const handleConfirmReject = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || !user.uid) {
      alert('Admin authentication required. Please log in again.');
      return;
    }

    try {
      await updateArtStatus(
        user.uid,
        pendingAction.artwork.artistDocID,
        pendingAction.artwork.artDocID,
        pendingAction.newStatus,
        reasonText
      );

      // Send decision email to artist
      try {
        await fetch(`${ENDPOINT_PATH}/api/artwork-decision-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            artistDocId: pendingAction.artwork.artistDocID,
            artDocId: pendingAction.artwork.artDocID,
            newStatus: pendingAction.newStatus,
            reason: reasonText
          })
        });
      } catch (emailError) {
        console.error('Error sending artwork decision email:', emailError);
        // Do not block the status update if email fails
      }

      await refreshArtworks();

      alert(`Artwork "${pendingAction.artwork.artworkName}" has been rejected!`);
      handleCloseReasonModal();
    } catch (error) {
      console.error('Error updating artwork status:', error);
      alert(`Error updating artwork status: ${error.message || 'Please try again.'}`);
    }
  };

  const handleConfirmDelete = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || !user.uid) {
      alert('Admin authentication required. Please log in again.');
      return;
    }

    try {
      await delArt(
        pendingAction.artwork.artistDocID,
        pendingAction.artwork.artDocID,
        reasonText
      );

      await refreshArtworks();

      alert(`Artwork "${pendingAction.artwork.artworkName}" has been deleted.`);
      handleCloseReasonModal();
    } catch (error) {
      console.error('Error deleting artwork:', error);
      alert(`Error deleting artwork: ${error.message || 'Please try again.'}`);
    }
  };

  const handleConfirmRejectApplication = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || !user.uid) {
      alert('Admin authentication required. Please log in again.');
      return;
    }

    const application = pendingAction.application;

    try {
      await updateApplicationStatus(
        user.uid,
        application.id,
        'rejected'
      );

      // Send rejection email with reason
      try {
        await fetch(`${ENDPOINT_PATH}/api/artist-application-decision`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            applicationId: application.id,
            newStatus: 'rejected',
            reason: reasonText
          })
        });
      } catch (emailError) {
        console.error('Error sending decision email:', emailError);
        // Do not block the status update if email fails
      }

      // Update local state
      setApplications((prevApplications) =>
        prevApplications.map((a) =>
          a.id === application.id
            ? { ...a, status: 'rejected' }
            : a
        )
      );

      // Refresh applications list
      try {
        const res = await fetch(`${ENDPOINT_PATH}/api/artist-applications`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && Array.isArray(data.data)) {
            setApplications(data.data);
          } else if (Array.isArray(data.data)) {
            setApplications(data.data);
          }
        }
      } catch (refreshError) {
        console.error('Error refreshing applications:', refreshError);
      }

      alert(`Application from "${application.name}" has been rejected!`);
      handleCloseReasonModal();
    } catch (error) {
      console.error('Error updating application status:', error);
      alert(`Error updating application status: ${error.message || 'Please try again.'}`);
    }
  };

  const handleConfirmReason = () => {
    if (isEmployee) return;
    if (!reasonText.trim()) {
      alert('Please provide a reason');
      return;
    }

    if (pendingAction?.type === 'reject') {
      handleConfirmReject();
    } else if (pendingAction?.type === 'delete') {
      handleConfirmDelete();
    } else if (pendingAction?.type === 'rejectApplication') {
      handleConfirmRejectApplication();
    }
  };

  return (
    <div>
      <AdminNavbar />
      <hr className="admin-divider" />
      <div className="artist-page">
        <Sidebar userType={userType || 'admin'} />
        <div className="artist-content">
          {isEmployee && (
            <div className="view-only-banner">
              <VisibilityIcon style={{ fontSize: '18px' }} />
              <span>Employee accounts have read-only access to artists and artwork.</span>
            </div>
          )}
          <div className="header">
            <div className="header-left">
              <button className={`btn ${activeTab === 'artists' ? 'active' : ''}`}
                onClick={() => { setActiveTab('artists'); setCurrentPage(1); }}
              >Artist Profile</button>
              <button className={`btn ${activeTab === 'artwork' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('artwork');
                  setCurrentPage(1);
                  setArtworkListPage(1);
                  setArtworkStatusTab('pending');
                }}
              >Artwork Profile</button>
              <button className={`btn ${activeTab === 'applications' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('applications');
                  setCurrentPage(1);
                  setApplicationListPage(1);
                  setApplicationStatusTab('pending');
                }}
              >Artist Applications</button>
            </div>

            <div className="header-right">
              <input
                type="text"
                className="search-box"
                placeholder="Search Artist or Artwork..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {!isEmployee && (
                <>
                  <button className="btn" onClick={() => { setEditingArtist(null); setShowArtistModal(true); }}>Add New Artist</button>
                  <button className="btn" onClick={() => { setEditingArtwork(null); setShowArtworkModal(true); }}>Add New Artwork</button>
                </>
              )}
            </div>
          </div>

          {/* Artwork: status sub-tabs (Pending | Approved | Rejected) — one table + one paginator to avoid vertical layout shift */}
          {activeTab === 'artwork' && (() => {
            const activeArtList = artworkSections[artworkStatusTab];
            const artworkTotalPages = Math.max(1, Math.ceil(activeArtList.length / itemsPerPage));
            const safeArtPage = Math.min(Math.max(1, artworkListPage), artworkTotalPages);
            const artSlice = activeArtList.slice((safeArtPage - 1) * itemsPerPage, safeArtPage * itemsPerPage);
            const sortKey = artworkSort.key;
            const sortDir = artworkSort.dir;
            return (
              <div className="artwork-profile-content admin-sortable-table-wrap">
                <div className="status-subtabs" role="tablist" aria-label="Artwork status">
                  {STATUS_SUBTABS.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={artworkStatusTab === key}
                      className={`btn status-subtab ${artworkStatusTab === key ? 'active' : ''}`}
                      onClick={() => {
                        setArtworkStatusTab(key);
                        setArtworkListPage(1);
                      }}
                    >
                      {label}
                      <span className="status-subtab-count">({artworkSections[key].length})</span>
                    </button>
                  ))}
                </div>
                {activeArtList.length === 0 ? (
                  <p className="text-muted mt-3 mb-0">
                    No {STATUS_SUBTABS.find((t) => t.key === artworkStatusTab)?.label.toLowerCase() || artworkStatusTab} artwork
                    {searchQuery ? ' matching your search' : ''}.
                  </p>
                ) : (
                  <>
                    <div className="artist-table-scroll">
                    <Table striped bordered hover className="artist-table mt-3">
                      <thead>
                        <tr>
                          <th style={{ width: '200px', textAlign: 'center' }}>IMAGE</th>
                          <th
                            scope="col"
                            aria-sort={tableColumnAriaSort(sortKey, sortDir, 'artworkName')}
                            className={`artwork-sort-th ${sortKey === 'artworkName' ? 'artwork-sort-th--active' : ''}`}
                          >
                            <ArtworkSortHeaderButton
                              label="ARTWORK NAME"
                              sortKey="artworkName"
                              activeKey={sortKey}
                              activeDir={sortDir}
                              onSort={handleArtworkSortClick}
                            />
                          </th>
                          <th
                            scope="col"
                            aria-sort={tableColumnAriaSort(sortKey, sortDir, 'artistName')}
                            className={`artwork-sort-th ${sortKey === 'artistName' ? 'artwork-sort-th--active' : ''}`}
                          >
                            <ArtworkSortHeaderButton
                              label="ARTIST"
                              sortKey="artistName"
                              activeKey={sortKey}
                              activeDir={sortDir}
                              onSort={handleArtworkSortClick}
                            />
                          </th>
                          <th
                            scope="col"
                            aria-sort={tableColumnAriaSort(sortKey, sortDir, 'category')}
                            className={`artwork-sort-th ${sortKey === 'category' ? 'artwork-sort-th--active' : ''}`}
                          >
                            <ArtworkSortHeaderButton
                              label="CATEGORY"
                              sortKey="category"
                              activeKey={sortKey}
                              activeDir={sortDir}
                              onSort={handleArtworkSortClick}
                            />
                          </th>
                          <th scope="col">DESCRIPTION</th>
                          <th
                            scope="col"
                            aria-sort={tableColumnAriaSort(sortKey, sortDir, 'submittedDate')}
                            className={`submitted-date-header artwork-sort-th ${
                              sortKey === 'submittedDate' ? 'artwork-sort-th--active' : ''
                            }`}
                          >
                            <ArtworkSortHeaderButton
                              label="SUBMITTED DATE"
                              sortKey="submittedDate"
                              activeKey={sortKey}
                              activeDir={sortDir}
                              onSort={handleArtworkSortClick}
                            />
                          </th>
                          <th scope="col">STATUS</th>
                          {!isEmployee && <th scope="col" className="artwork-actions-header">ACTIONS</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {artSlice.map((artwork) => {
                          const artworkStatus = artwork.status || 'pending';
                          const statusLower = artworkStatus.toLowerCase();
                          const isPending = statusLower === 'pending';
                          const isApproved = statusLower === 'approved';
                          const isRejected = statusLower === 'rejected';
                          return (
                            <tr key={`${artwork.artistDocID}-${artwork.artDocID}`}>
                              <td style={{ width: '200px', textAlign: 'center' }}>
                                <img src={artwork.artImageUrl} alt={artwork.artworkName} className="artwork-image" />
                              </td>
                              <td>{artwork.artworkName}</td>
                              <td>{artwork.artistName}</td>
                              <td>{artwork.category}</td>
                              <td className="artwork-description-cell">
                                <span
                                  className="artwork-desc-cell"
                                  title={artwork.artDescription?.trim() ? artwork.artDescription : undefined}
                                >
                                  {artwork.artDescription?.trim() ? artwork.artDescription : '—'}
                                </span>
                              </td>
                              <td className="submitted-date-cell">{formatSubmittedDate(artwork.updatedAt)}</td>
                              <td>
                                <span className={getStatusBadgeClass(artworkStatus)}>
                                  {getStatusText(artworkStatus)}
                                </span>
                              </td>
                              {!isEmployee && (
                                <td className="artwork-actions-cell">
                                  <div className="d-flex align-items-center gap-2 artwork-actions-inner">
                                    <button type="button" className="icon-button" onClick={() => handleEditArtwork(artwork)}>
                                      <EditIcon style={{ color: 'black' }} />
                                    </button>
                                    <button type="button" className="icon-button" onClick={() => handleDeleteArtwork(artwork)}>
                                      <DeleteIcon className="delete-icon" />
                                    </button>
                                    {(isPending || isRejected) && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-success"
                                        onClick={() => handleUpdateArtStatus(artwork, 'approved')}
                                        title={isPending ? 'Approve artwork' : 'Change to Approved'}
                                      >
                                        Approve
                                      </button>
                                    )}
                                    {(isPending || isApproved) && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleUpdateArtStatus(artwork, 'rejected')}
                                        title={isPending ? 'Reject artwork' : 'Change to Rejected'}
                                      >
                                        Reject
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    </div>
                    <div className="pagination">
                      <button
                        type="button"
                        className="page-btn"
                        onClick={() => setArtworkListPage((p) => Math.max(1, p - 1))}
                        disabled={safeArtPage <= 1}
                      >
                        Prev
                      </button>
                      <span>
                        Showing {(safeArtPage - 1) * itemsPerPage + 1} - {Math.min(safeArtPage * itemsPerPage, activeArtList.length)} of {activeArtList.length}
                      </span>
                      <button
                        type="button"
                        className="page-btn"
                        onClick={() => setArtworkListPage((p) => Math.min(artworkTotalPages, p + 1))}
                        disabled={safeArtPage >= artworkTotalPages}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {activeTab === 'artists' && (() => {
            const artistSortKey = artistSort.key;
            const artistSortDir = artistSort.dir;
            return (
            <div className="artist-table-container admin-sortable-table-wrap">
              <Table striped bordered hover className='artist-table'>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      aria-sort={tableColumnAriaSort(artistSortKey, artistSortDir, 'name')}
                      className={`artwork-sort-th ${artistSortKey === 'name' ? 'artwork-sort-th--active' : ''}`}
                    >
                      <ArtworkSortHeaderButton
                        label="NAME"
                        sortKey="name"
                        activeKey={artistSortKey}
                        activeDir={artistSortDir}
                        onSort={handleArtistSortClick}
                      />
                    </th>
                    <th scope="col">PHONE NUMBER</th>
                    <th
                      scope="col"
                      aria-sort={tableColumnAriaSort(artistSortKey, artistSortDir, 'email')}
                      className={`artwork-sort-th ${artistSortKey === 'email' ? 'artwork-sort-th--active' : ''}`}
                    >
                      <ArtworkSortHeaderButton
                        label="EMAIL"
                        sortKey="email"
                        activeKey={artistSortKey}
                        activeDir={artistSortDir}
                        onSort={handleArtistSortClick}
                      />
                    </th>
                    {!isEmployee && <th scope="col">ACTIONS</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredArtists
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((artist) => (
                      <tr key={artist.id}>
                        <td>{`${artist.firstName} ${artist.lastName}`}</td>
                        <td>{artist.phoneNumber}</td>
                        <td>{artist.email}</td>
                        {!isEmployee && (
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <button className="icon-button" onClick={() => handleEditArtist(artist)}>
                                <EditIcon style={{ color: 'black' }} />
                              </button>
                              <button className="icon-button" onClick={() => handleDeleteArtist(artist)}>
                                <DeleteIcon className="delete-icon" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
            );
          })()}

          {activeTab === 'applications' && (() => {
            if (applicationsLoading) {
              return (
                <div className="artist-table-container">
                  <p>Loading artist applications...</p>
                </div>
              );
            }
            if (applicationsError) {
              return (
                <div className="artist-table-container">
                  <p style={{ color: 'red' }}>{applicationsError}</p>
                </div>
              );
            }
            if (applications.length === 0) {
              return (
                <div className="artist-table-container">
                  <p>No artist applications found.</p>
                </div>
              );
            }
            if (
              applicationSections.pending.length === 0 &&
              applicationSections.approved.length === 0 &&
              applicationSections.rejected.length === 0
            ) {
              return (
                <div className="artist-table-container">
                  <p>No matching applications for your search.</p>
                </div>
              );
            }
            const activeAppList = applicationSections[applicationStatusTab];
            const appTotalPages = Math.max(1, Math.ceil(activeAppList.length / itemsPerPage));
            const safeAppPage = Math.min(Math.max(1, applicationListPage), appTotalPages);
            const appSlice = activeAppList.slice((safeAppPage - 1) * itemsPerPage, safeAppPage * itemsPerPage);
            const appSortKey = applicationSort.key;
            const appSortDir = applicationSort.dir;
            return (
              <div className="artist-table-container admin-sortable-table-wrap">
                <div className="status-subtabs" role="tablist" aria-label="Application status">
                  {STATUS_SUBTABS.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={applicationStatusTab === key}
                      className={`btn status-subtab ${applicationStatusTab === key ? 'active' : ''}`}
                      onClick={() => {
                        setApplicationStatusTab(key);
                        setApplicationListPage(1);
                      }}
                    >
                      {label}
                      <span className="status-subtab-count">({applicationSections[key].length})</span>
                    </button>
                  ))}
                </div>
                {activeAppList.length === 0 ? (
                  <p className="text-muted mt-3 mb-0">
                    No {STATUS_SUBTABS.find((t) => t.key === applicationStatusTab)?.label.toLowerCase() || applicationStatusTab} applications
                    {searchQuery ? ' matching your search' : ''}.
                  </p>
                ) : (
                  <>
                    <div className="artist-table-scroll">
                    <Table striped bordered hover className="artist-table mt-3">
                      <thead>
                        <tr>
                          <th
                            scope="col"
                            aria-sort={tableColumnAriaSort(appSortKey, appSortDir, 'name')}
                            className={`artwork-sort-th ${appSortKey === 'name' ? 'artwork-sort-th--active' : ''}`}
                          >
                            <ArtworkSortHeaderButton
                              label="NAME"
                              sortKey="name"
                              activeKey={appSortKey}
                              activeDir={appSortDir}
                              onSort={handleApplicationSortClick}
                            />
                          </th>
                          <th
                            scope="col"
                            aria-sort={tableColumnAriaSort(appSortKey, appSortDir, 'email')}
                            className={`artwork-sort-th ${appSortKey === 'email' ? 'artwork-sort-th--active' : ''}`}
                          >
                            <ArtworkSortHeaderButton
                              label="EMAIL"
                              sortKey="email"
                              activeKey={appSortKey}
                              activeDir={appSortDir}
                              onSort={handleApplicationSortClick}
                            />
                          </th>
                          <th scope="col">PHONE NUMBER</th>
                          <th scope="col">BIO PREVIEW</th>
                          <th
                            scope="col"
                            aria-sort={tableColumnAriaSort(appSortKey, appSortDir, 'submittedDate')}
                            className={`submitted-date-header artwork-sort-th ${
                              appSortKey === 'submittedDate' ? 'artwork-sort-th--active' : ''
                            }`}
                          >
                            <ArtworkSortHeaderButton
                              label="SUBMITTED DATE"
                              sortKey="submittedDate"
                              activeKey={appSortKey}
                              activeDir={appSortDir}
                              onSort={handleApplicationSortClick}
                            />
                          </th>
                          <th scope="col">STATUS</th>
                          <th scope="col">ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appSlice.map((app) => {
                          const applicationStatus = app.status || 'pending';
                          const bioPreview = app.artistBio
                            ? (app.artistBio.length > 100 ? app.artistBio.substring(0, 100) + '...' : app.artistBio)
                            : '-';
                          return (
                            <tr key={app.id}>
                              <td>{app.name}</td>
                              <td>{app.email}</td>
                              <td>{app.phoneNumber || '-'}</td>
                              <td style={{ maxWidth: '300px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                {bioPreview}
                              </td>
                              <td className="submitted-date-cell">{formatSubmittedDate(app.updatedAt)}</td>
                              <td>
                                <span className={getStatusBadgeClass(applicationStatus)}>
                                  {applicationStatus.charAt(0).toUpperCase() + applicationStatus.slice(1)}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2 justify-content-center">
                                  <button
                                    type="button"
                                    className="icon-button"
                                    onClick={() => {
                                      setSelectedApplication(app);
                                      setShowApplicationModal(true);
                                    }}
                                    title="View full application"
                                  >
                                    <VisibilityIcon style={{ color: 'black' }} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    </div>
                    <div className="pagination">
                      <button
                        type="button"
                        className="page-btn"
                        onClick={() => setApplicationListPage((p) => Math.max(1, p - 1))}
                        disabled={safeAppPage <= 1}
                      >
                        Prev
                      </button>
                      <span>
                        Showing {(safeAppPage - 1) * itemsPerPage + 1} - {Math.min(safeAppPage * itemsPerPage, activeAppList.length)} of {activeAppList.length}
                      </span>
                      <button
                        type="button"
                        className="page-btn"
                        onClick={() => setApplicationListPage((p) => Math.min(appTotalPages, p + 1))}
                        disabled={safeAppPage >= appTotalPages}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* Pagination: Artist Profile tab only */}
          {activeTab === 'artists' && (
            <div className="pagination">
              <button
                type="button"
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span>
                {(() => {
                  const currentLength = getCurrentDataLength();
                  const start = currentLength === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
                  const end = Math.min(currentPage * itemsPerPage, currentLength);
                  return `Showing ${start} - ${end} of ${currentLength}`;
                })()}
              </span>
              <button
                type="button"
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || getCurrentDataLength() === 0}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal for New Artist Profile */}
      <ArtistProfileModal handleImageUpload={handleImageUpload} />
      {/* Modal for New Artwork */}
      <ArtworkModal handleImageUpload={handleImageUpload} onArtworksMutated={refreshArtworks} />

      {/* Modal for Viewing Application Details */}
      <Modal show={showApplicationModal} onHide={() => setShowApplicationModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Artist Application Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApplication && (
            <div>
              {/* Profile Photo at the top */}
              {(selectedApplication.artistPhotoURL || selectedApplication.artistPhotoUrl) && (
                <div className="mb-4 text-center">
                  <strong>Profile Photo:</strong>
                  <div className="mt-2 d-flex justify-content-center">
                    <img
                      src={selectedApplication.artistPhotoURL || selectedApplication.artistPhotoUrl}
                      alt="Profile"
                      style={{
                        maxWidth: '300px',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="mb-3">
                <strong>Name:</strong> {selectedApplication.name}
              </div>
              <div className="mb-3">
                <strong>Email:</strong> {selectedApplication.email}
              </div>
              <div className="mb-3">
                <strong>Phone Number:</strong> {selectedApplication.phoneNumber || 'Not provided'}
              </div>
              <div className="mb-3">
                <strong>Social Links:</strong> {selectedApplication.socialLinks || 'Not provided'}
              </div>
              <div className="mb-3">
                <strong>Short Bio:</strong>
                <p style={{ whiteSpace: 'pre-wrap', marginTop: '5px' }}>{selectedApplication.artistBio}</p>
              </div>
              {selectedApplication.resaleCertificate && (
                <div className="mb-3">
                  <strong>Resale Certificate:</strong>
                  <div className="mt-2">
                    <a
                      href={selectedApplication.resaleCertificate}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-primary"
                    >
                      View Certificate
                    </a>
                  </div>
                </div>
              )}
              <div className="mb-3">
                <strong>Status:</strong>
                <span className={`ms-2 ${getStatusBadgeClass(selectedApplication.status || 'pending')}`}>
                  {(selectedApplication.status || 'pending').charAt(0).toUpperCase() + (selectedApplication.status || 'pending').slice(1)}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApplicationModal(false)}>
            Close
          </Button>
          {selectedApplication && !isEmployee && (() => {
            const appStatus = (selectedApplication.status || 'pending').toLowerCase();
            const isPending = appStatus === 'pending';
            const isApproved = appStatus === 'approved';
            const isRejected = appStatus === 'rejected';

            return (
              <>
                {isPending && (
                  <>
                    <Button
                      variant="success"
                      onClick={() => {
                        handleUpdateApplicationStatus(selectedApplication, 'approved');
                        setShowApplicationModal(false);
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => {
                        setShowApplicationModal(false);
                        handleUpdateApplicationStatus(selectedApplication, 'rejected');
                      }}
                    >
                      Reject
                    </Button>
                  </>
                )}
                {isApproved && (
                  <Button
                    variant="warning"
                    onClick={() => {
                      handleUpdateApplicationStatus(selectedApplication, 'pending');
                      setShowApplicationModal(false);
                    }}
                  >
                    Reset to Pending
                  </Button>
                )}
                {isRejected && (
                  <Button
                    variant="warning"
                    onClick={() => {
                      handleUpdateApplicationStatus(selectedApplication, 'pending');
                      setShowApplicationModal(false);
                    }}
                  >
                    Reset to Pending
                  </Button>
                )}
              </>
            );
          })()}
        </Modal.Footer>
      </Modal>

      {/* Single Reason Modal for Reject/Delete artwork and Reject application */}
      <Modal show={showReasonModal} onHide={handleCloseReasonModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {pendingAction?.type === 'reject'
              ? 'Reject Artwork'
              : pendingAction?.type === 'delete'
                ? 'Delete Artwork'
                : pendingAction?.type === 'rejectApplication'
                  ? 'Reject Application'
                  : 'Provide Reason'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label htmlFor="reasonTextarea" className="form-label" style={{ marginTop: '15px' }}>
              Reason <span className="text-danger">*</span>
            </label>
            <textarea
              id="reasonTextarea"
              className="form-control"
              rows="4"
              placeholder="Please provide a reason for rejecting/removing this artwork..."
              value={reasonText}
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue.length <= 500) {
                  setReasonText(newValue);
                }
              }}
              maxLength={500}
              required
              disabled={isEmployee}
            />
            <small className="text-muted">
              {reasonText.length}/500 characters
            </small>
          </div>
          {pendingAction?.artwork && (
            <div className="alert alert-info">
              <strong>Artwork:</strong> {pendingAction.artwork.artworkName}
            </div>
          )}
          {pendingAction?.application && (
            <div className="alert alert-info">
              <strong>Applicant:</strong> {pendingAction.application.name}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseReasonModal}>
            Cancel
          </Button>
          {!isEmployee && (
            <Button variant="danger" onClick={handleConfirmReason}>
              {pendingAction?.type === 'reject'
                ? 'Reject'
                : pendingAction?.type === 'delete'
                  ? 'Delete'
                  : 'Reject'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );

};
export default ArtistPage;